import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Zap, Clock, Users, ArrowLeft, Share2, Shield, Truck, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { flashDealsAPI, paymentsAPI } from '../lib/api';
import { formatCurrency, formatTimeRemaining } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function FlashDealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const res = await flashDealsAPI.getById(id);
        setDeal(res.data);
        setTimeLeft(formatTimeRemaining(res.data.end_time));
      } catch (error) {
        console.error('Error fetching deal:', error);
        toast.error('Flash deal not found');
        navigate('/live');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDeal();
  }, [id, navigate]);

  useEffect(() => {
    if (!deal) return;
    
    const interval = setInterval(() => {
      setTimeLeft(formatTimeRemaining(deal.end_time));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [deal]);

  const handleClaim = async () => {
    if (!user) {
      toast.error('Please login to claim this deal');
      navigate('/login');
      return;
    }
    
    setClaiming(true);
    try {
      const res = await flashDealsAPI.claim(id);
      toast.success(`Deal claimed! You earned ${res.data.points_earned} points!`);
      
      // Redirect to payment
      const paymentRes = await paymentsAPI.createCheckout(res.data.order_id);
      window.location.href = paymentRes.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim deal');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!deal) return null;

  const claimsPercent = (deal.claims_count / deal.max_claims) * 100;
  const isExpired = timeLeft?.expired;
  const isSoldOut = deal.claims_count >= deal.max_claims;
  const isUrgent = claimsPercent > 70 || timeLeft?.urgent;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-zinc-100 rounded-full transition-colors" data-testid="back-btn">
          <ArrowLeft size={20} className="text-zinc-700" />
        </button>
        <div className={cn(
          "px-4 py-1.5 rounded-full flex items-center gap-2",
          timeLeft?.urgent ? "bg-red-500 text-white" : "bg-zinc-100 text-zinc-900"
        )}>
          <Clock size={14} />
          <span className="font-mono font-bold text-sm">{timeLeft?.text}</span>
        </div>
        <button className="p-2 hover:bg-zinc-100 rounded-full transition-colors" data-testid="share-btn">
          <Share2 size={20} className="text-zinc-700" />
        </button>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 p-4 md:p-8">
          {/* Deal Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-zinc-200"
          >
            {deal.product_image ? (
              <img 
                src={deal.product_image} 
                alt={deal.product_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#b8ff00]/20 to-[#00ff88]/20">
                <Zap size={80} className="text-[#b8ff00]" />
              </div>
            )}
            
            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <div className="discount-badge px-4 py-2 rounded-full">
                <span className="font-display font-bold text-lg">-{deal.discount_percent}%</span>
              </div>
              <div className="bg-zinc-900 text-white px-3 py-1.5 rounded-full flex items-center gap-1.5">
                <Flame size={14} className="text-orange-400" />
                <span className="text-xs font-semibold">Flash Deal</span>
              </div>
            </div>
          </motion.div>

          {/* Deal Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col"
          >
            <p className="text-sm text-zinc-500 mb-2">by {deal.vendor_name}</p>
            <h1 className="font-display font-bold text-3xl text-zinc-900 mb-4">{deal.product_name}</h1>
            
            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-display font-bold text-4xl gradient-text" data-testid="flash-price">
                {formatCurrency(deal.flash_price)}
              </span>
              <span className="text-xl text-zinc-400 line-through">
                {formatCurrency(deal.original_price)}
              </span>
            </div>
            
            <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full inline-flex items-center gap-2 w-fit mb-6">
              <span className="font-semibold">You save {formatCurrency(deal.original_price - deal.flash_price)}</span>
            </div>
            
            {/* Inventory Progress */}
            <div className="mb-6 p-4 bg-white rounded-2xl border border-zinc-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-zinc-500" />
                  <span className="text-sm text-zinc-600">{deal.claims_count} claimed</span>
                </div>
                <span className={cn("text-sm font-bold", isUrgent ? "text-orange-500" : "text-zinc-900")}>
                  {deal.max_claims - deal.claims_count} left!
                </span>
              </div>
              <div className="inventory-bar">
                <div 
                  className={cn("inventory-fill", isUrgent && "urgent")}
                  style={{ width: `${claimsPercent}%` }}
                />
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <Shield size={16} className="text-green-500" />
                <span>Buyer Protection</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <Truck size={16} className="text-blue-500" />
                <span>Fast Shipping</span>
              </div>
            </div>
            
            {/* Claim Button */}
            <Button
              onClick={handleClaim}
              disabled={claiming || isExpired || isSoldOut}
              className={cn(
                "w-full font-bold text-base py-6 rounded-xl transition-all",
                isExpired || isSoldOut 
                  ? 'bg-zinc-200 text-zinc-500 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black hover:shadow-lg hover:shadow-[#b8ff00]/30'
              )}
              data-testid="claim-deal-btn"
            >
              {claiming ? 'Claiming...' : isExpired ? 'Deal Expired' : isSoldOut ? 'Sold Out' : 'Claim This Deal'}
            </Button>
            
            {!user && !isExpired && !isSoldOut && (
              <p className="text-center text-sm text-zinc-500 mt-3">
                Please <button onClick={() => navigate('/login')} className="font-semibold text-zinc-900 hover:text-[#00aa55]">login</button> to claim
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
